import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../Router/all_routes";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import {
  Plus,
  ShoppingCart,
  Download,
  Printer,
  Trash2,
  Calendar,
  FileText,
  Save,
  ArrowLeft,
  Package,
  Layers,
  ScanBarcode,
  Settings,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { ReactSelect } from "@/components/ui/react-select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import AddSerialsModal from "../inventory/components/AddSerialsModal";

const EditGRN = () => {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams();
  const store_id = useSelector((state) => state.current_store?.id || null);

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";



  const [grn, setGrn] = useState({});
  const [grnItems, setGrnItems] = useState([]);
  const [employeeSearchInput, setEmployeeSearchInput] = useState("");
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeLabels, setEmployeeLabels] = useState({});
  const [poSearchInput, setPoSearchInput] = useState("");
  const [poSearchTerm, setPoSearchTerm] = useState("");

  const [productSearchInput, setProductSearchInput] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productLabels, setProductLabels] = useState({});

  const [configModal, setConfigModal] = useState({
    isOpen: false,
    itemIndex: null,
  });

  // Serial Modal State
  const [serialModal, setSerialModal] = useState({
    isOpen: false,
    itemIndex: null,
    productName: "",
    quantity: 0,
    initialSerials: [],
  });

  const [statusConfirmModal, setStatusConfirmModal] = useState({
    isOpen: false,
    targetStatus: null,
    isFormSubmit: false,
    pendingPayload: null,
  });

  const openSerialModal = (index) => {
    const item = grnItems[index];

    // Try to find product in grnItems if it's already there
    const product =
      item.product ||
      orderProducts.find(
        (p) => String(p.product_id) === String(item.product_id),
      )?.product ||
      searchedProductsData.find(
        (p) => String(p.id) === String(item.product_id),
      );

    const remainingToReceive =
      (parseInt(item.order_quantity) || 0) -
      (parseInt(item.already_received_quantity) || 0);

    setSerialModal({
      isOpen: true,
      itemIndex: index,
      productName: product?.name || product?.product_name || "Product",
      quantity: remainingToReceive > 0 ? remainingToReceive : 999999,
      initialSerials: item.serial_numbers || [],
    });
  };

  const handleSaveSerials = (serials) => {
    grnItemChange(serialModal.itemIndex, "serial_numbers", serials);
  };

  // Memoized list of serials used in other items to prevent duplicates
  const excludeSerials = React.useMemo(() => {
    return grnItems.flatMap((item, idx) =>
      idx === serialModal.itemIndex ? [] : item.serial_numbers || [],
    );
  }, [grnItems, serialModal.itemIndex]);

  // Queries
  const { data: availablePaymentMethods = [], isLoading: paymentMethodsLoading } = useQuery({
    queryKey: ["payment-methods-available", "purchases", store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "purchases",
          store_id: store_id || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
  });
  const { data: grnData, isLoading: grnLoading } = useQuery({
    queryKey: ["grn", id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PURCHASES.GRN_DETAILS(id), {
        params: {
          with: [
            "purchaseOrder",
            "store",
            "createdBy",
            "receivedBy",
            "items",
            "items.product",
            "items.batch",
            "paymentMethod",
            "items.batch.serials",
          ],
        },
      });
      return res.data.data;
    },
    enabled: !!id,
  });

  const isCompleted = grnData?.status?.toLowerCase() === "completed";

  // Use items directly from grnData
  const rawItems = grnData?.items;
  const itemsData = React.useMemo(() => rawItems || [], [rawItems]);
  const itemsLoading = grnLoading;

  const { data: ordersData, isFetching: isOrdersFetching } = useQuery({
    queryKey: ["purchaseOrders", poSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PURCHASES.ORDERS, {
        params: {
          store_id,
          with: ["items.product"],
          search: poSearchTerm || undefined,
          per_page: 15,
          perPage: 15,
        },
      });
      return res.data.data;
    },
    enabled:
      hasPermission("purchase order read") &&
      hasPermission("product read") &&
      hasPermission("supplier read"),
  });

  // Merge fetched orders with the currently selected order so the selected
  // value is always present in the options list even after the search changes.
  const orders = React.useMemo(() => {
    const fetched = ordersData || [];
    const currentPO = grnData?.purchaseOrder || grnData?.purchase_order;
    const currentPOId = grn.purchase_order_id || currentPO?.id;
    if (!currentPOId) return fetched;
    const alreadyIncluded = fetched.some(
      (o) => String(o.id) === String(currentPOId),
    );
    if (alreadyIncluded) return fetched;
    if (currentPO) return [currentPO, ...fetched];
    // Try to find the selected order from a previous fetch stored in the query cache
    const cached = queryClient.getQueryData(["purchaseOrders", ""]);
    const selectedOrder = (cached || []).find(
      (o) => String(o.id) === String(currentPOId),
    );
    return selectedOrder ? [selectedOrder, ...fetched] : fetched;
  }, [ordersData, grn.purchase_order_id, grnData, queryClient]);

  const purchaseOrderId = grn.purchase_order_id;

  // Derive orderProducts from the selected order
  const orderProducts = React.useMemo(() => {
    if (!purchaseOrderId) return [];
    const selectedOrder = orders.find(
      (o) => String(o.id) === String(purchaseOrderId),
    );
    return selectedOrder?.items || [];
  }, [orders, purchaseOrderId]);

  // Sync PO reference/notes to GRN if not already set (mostly for new conversions)
  useEffect(() => {
    if (!purchaseOrderId) return;
    const selectedOrder = orders.find(
      (o) => String(o.id) === String(purchaseOrderId),
    );
    if (selectedOrder && !grn.reference && orders.length > 0) {
      setGrn((prev) => ({
        ...prev,
        reference:
          prev.reference ||
          selectedOrder.tracking_number ||
          selectedOrder.reference ||
          "",
        notes:
          prev.notes ||
          selectedOrder.internal_notes ||
          selectedOrder.public_notes ||
          "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, purchaseOrderId]);

  const { data: employees = [], isFetching: isEmployeesFetching } = useQuery({
    queryKey: ["employees", employeeSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES, {
        params: {
          search: employeeSearchTerm || undefined,
          perPage: 15,
          per_page: 15,
        },
      });
      return res.data.data;
    },
    enabled: hasPermission("employee read"),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setEmployeeSearchTerm(employeeSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPoSearchTerm(poSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [poSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProductSearchTerm(productSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchInput]);

  // Server-side product search query - only searches when user enters a search term
  const { data: searchedProductsData = [], isFetching: isProductsFetching } =
    useQuery({
      queryKey: ["inventory-products", productSearchTerm],
      queryFn: async () => {
        const res = await apiClient.get(API_ENDPOINTS.INVENTORY.PRODUCTS, {
          params: {
            search: productSearchTerm,
            per_page: 25,
            perPage: 25,
          },
        });
        return res.data?.data || res.data || [];
      },
      enabled:
        hasPermission("product read") &&
        Boolean(productSearchTerm && productSearchTerm.trim().length > 0),
    });

  useEffect(() => {
    if (orderProducts.length > 0) {
      setProductLabels((prev) => {
        const next = { ...prev };
        orderProducts.forEach((p) => {
          if (p.product) {
            next[String(p.product_id)] = `${p.product.name || p.product.product_name || ""}${p.product.sku ? ` (${p.product.sku})` : ""}`;
          }
        });
        return next;
      });
    }
  }, [orderProducts]);

  useEffect(() => {
    if (searchedProductsData.length > 0) {
      setProductLabels((prev) => {
        const next = { ...prev };
        searchedProductsData.forEach((p) => {
          next[String(p.id)] = `${p.name || p.product_name || ""}${p.sku ? ` (${p.sku})` : ""}`;
        });
        return next;
      });
    }
  }, [searchedProductsData]);

  // Product options: by default only show PO items; show server search results only when actively searching
  const productOptions = useMemo(() => {
    const isSearching = Boolean(
      productSearchInput && productSearchInput.trim().length > 0,
    );

    // 1. PO Products
    const filteredPoItems = orderProducts.filter((p) => {
      if (!isSearching) return true;
      const term = productSearchInput.toLowerCase().trim();
      const name = (p.product?.name || p.product?.product_name || "").toLowerCase();
      const sku = (p.product?.sku || "").toLowerCase();
      return name.includes(term) || sku.includes(term);
    });

    const poOptions = filteredPoItems.map((p) => ({
      value: String(p.product_id),
      label: `${p.product?.name || p.product?.product_name || ""}${p.product?.sku ? ` (${p.product.sku})` : ""}`,
      product: p.product,
      isPoItem: true,
      poItem: p,
    }));

    // If NOT searching, only return PO items (clean and uncluttered)
    if (!isSearching) {
      if (poOptions.length > 0) {
        return [
          {
            label: "PO Items",
            options: poOptions,
          },
        ];
      }
      return [];
    }

    // When actively searching:
    const groups = [];

    if (poOptions.length > 0) {
      groups.push({
        label: `PO Items (${poOptions.length})`,
        options: poOptions,
      });
    }

    // Exclude PO items from general search results to avoid duplicate options
    const nonPoProducts = searchedProductsData.filter(
      (prod) =>
        !orderProducts.some((po) => String(po.product_id) === String(prod.id)),
    );

    if (nonPoProducts.length > 0) {
      groups.push({
        label: `Other Products (${nonPoProducts.length})`,
        options: nonPoProducts.map((prod) => ({
          value: String(prod.id),
          label: `${prod.name || prod.product_name || ""}${prod.sku ? ` (${prod.sku})` : ""}`,
          product: prod,
          isPoItem: false,
          poItem: null,
        })),
      });
    }

    return groups;
  }, [orderProducts, searchedProductsData, productSearchInput]);

  useEffect(() => {
    if (grnData?.receivedBy) {
      const rec = grnData.receivedBy;
      if (rec.full_name || rec.name) {
        setEmployeeLabels((prev) => ({
          ...prev,
          [String(rec.id || grnData.received_by)]: rec.full_name || rec.name,
        }));
      }
    }
  }, [grnData]);

  useEffect(() => {
    if (employees.length > 0) {
      setEmployeeLabels((prev) => {
        const next = { ...prev };
        employees.forEach((emp) => {
          next[String(emp.id)] = emp.name || emp.full_name;
        });
        return next;
      });
    }
  }, [employees]);

  // Sync state
  useEffect(() => {
    if (grnData) {
      setGrn({
        ...grnData,
        store_id: grnData.store_id || grnData.store?.id || store_id,
        purchase_order_id:
          grnData.purchase_order_id ||
          grnData.purchase_order?.id ||
          grnData.purchaseOrder?.id ||
          "",
        received_by:
          grnData.received_by?.id ||
          grnData.received_by ||
          grnData.receivedBy?.id ||
          "",
        received_date: grnData.received_date
          ? grnData.received_date.split("T")[0]
          : "",
        payment_method:
          grnData.payment_method?.slug ||
          grnData.payment_method?.id ||
          grnData.paymentMethod?.slug ||
          grnData.paymentMethod?.id ||
          (typeof grnData.payment_method === "string"
            ? grnData.payment_method
            : null) ||
          grnData.payment_method_id ||
          null,
      });
    }
  }, [grnData, store_id]);

  useEffect(() => {
    if (itemsData && itemsData.length > 0) {
      const mapped = itemsData.map((it) => {
        const batch = it.batch || it.inventory_batch;
        const serials =
          Array.isArray(it.serial_numbers) && it.serial_numbers.length > 0
            ? it.serial_numbers
            : Array.isArray(batch?.serials)
              ? batch.serials
                .map((s) =>
                  typeof s === "object" ? s.serial_number || s.serial : s,
                )
                .filter(Boolean)
              : [];
        return {
          ...it,
          batch_number: it.batch_number || batch?.batch_number || "",
          expiry_date:
            it.expiry_date ||
            (batch?.expiry_date ? batch.expiry_date.split("T")[0] : "") ||
            "",
          serial_numbers: serials,
        };
      });
      setGrnItems(mapped);
      setProductLabels((prev) => {
        const next = { ...prev };
        itemsData.forEach((it) => {
          if (it.product) {
            next[String(it.product_id)] = `${it.product.name || it.product.product_name || ""}${it.product.sku ? ` (SKU: ${it.product.sku})` : ""}`;
          }
        });
        return next;
      });
    }
  }, [itemsData]);

  // Mutations
  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus) => {
      const res = await apiClient.patch(
        API_ENDPOINTS.PURCHASES.GRN_STATUS(id),
        { status: newStatus },
      );
      return res.data;
    },
    onSuccess: (data, newStatus) => {
      toast.success(`GRN status updated to ${newStatus}`);
      queryClient.invalidateQueries({ queryKey: ["grn", id] });
      queryClient.invalidateQueries({ queryKey: ["grns"] });
      setGrn((prev) => ({ ...prev, status: newStatus }));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update GRN status",
      );
    },
  });

  const updateGrnMutation = useMutation({
    mutationFn: async (updatedGrn) => {
      const initialStatus = grnData?.status?.toLowerCase();
      const nextStatus = updatedGrn.status?.toLowerCase();
      if (
        hasPermission("goods received note update_status") &&
        nextStatus &&
        initialStatus &&
        nextStatus !== initialStatus
      ) {
        await apiClient.patch(API_ENDPOINTS.PURCHASES.GRN_STATUS(id), {
          status: updatedGrn.status,
        });
      }

      const payload = {
        ...updatedGrn,
        store_id,
        received_by: updatedGrn.received_by || null,
        received_date: updatedGrn.received_date || null,
        status: updatedGrn.status || "pending",
        document: updatedGrn.document || "",
        document_type: updatedGrn.document_type || "",
        document_status: updatedGrn.document_status || "",
        document_url: updatedGrn.document_url || "",
        payment_method: updatedGrn.payment_method || null,
        sub_total_amount: parseFloat(updatedGrn.sub_total_amount || 0),
        total_tax: parseFloat(updatedGrn.total_tax || 0),
        total_amount: parseFloat(updatedGrn.total_amount || 0),
        meta: grn.meta || [],
        items: grnItems.map((item) => ({
          product_id: item.product_id,
          received_quantity: parseFloat(item.received_quantity) || 0,
          received_quantity_type: item.received_quantity_type || "good",
          order_quantity: parseFloat(item.order_quantity) || 0,
          already_received_quantity:
            parseFloat(item.already_received_quantity) || 0,
          unit_cost: parseFloat(item.unit_cost) || 0,
          unit_sell_price:
            parseFloat(item.unit_sell_price || item.unit_cost) || 0,
          unit_sell_price_min:
            parseFloat(item.unit_sell_price_min || item.unit_cost) || 0,
          unit_sell_price_max:
            parseFloat(item.unit_sell_price_max || item.unit_cost) || 0,
          tax_class: item.tax_class || "",
          tax_percentage: parseFloat(item.tax_percentage || 0),
          tax_amount: parseFloat(item.tax_amount) || 0,
          tax_method: (item.tax_method || "exclusive").toLowerCase(),
          discount_type: (item.discount_type || "fixed").toLowerCase(),
          discount_value: parseFloat(item.discount_value) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0,
          is_foc: item.is_foc || false,
          total_cost: parseFloat(item.total_cost) || 0,
          batch_number: item.batch_number || null,
          expiry_date: item.expiry_date || null,
          serial_numbers: item.serial_numbers || [],
          meta: item.meta || [],
          notes: item.notes || "",
        })),
      };

      await apiClient.put(API_ENDPOINTS.PURCHASES.GRN_DETAILS(id), payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grns"] });
      queryClient.invalidateQueries({ queryKey: ["grn", id] });
      queryClient.invalidateQueries({ queryKey: ["grnItems", id] });
      toast.success("GRN Updated Successfully");
      // Optional: Navigate back
      // navigate(all_routes.grn);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update GRN");
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (itemId) => {
      await apiClient.delete(API_ENDPOINTS.PURCHASES.GRN_ITEM_DETAILS(id, itemId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grnItems", id] });
      toast.success("Item removed successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to remove item");
    },
  });

  // PDF Generation
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleGeneratePDF = async () => {
    setPdfLoading(true);
    toast.info("Generating PDF...");
    try {
      const response = await apiClient.get(API_ENDPOINTS.REPORTS.GRN_REPORT(id), {
        headers: { Accept: "application/pdf" },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowPdfModal(true);
      setPdfLoading(false);
    } catch (error) {
      console.error(error);
      setPdfLoading(false);
      toast.error("Failed to generate PDF");
    }
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `GRN-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF downloaded");
    }
  };

  const handlePrintPDF = () => {
    if (pdfUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove the iframe after the print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
    }
  };

  // Handlers
  const validateFields = () => {
    const effectiveStoreId = grn.store_id || store_id;
    if (!grn.purchase_order_id || !effectiveStoreId) {
      toast.error("Please fill all required fields");
      return false;
    }
    return true;
  };

  const handleUpdateStatusOnlyClick = () => {
    if (isCompleted) {
      toast.error("Completed Goods Received Notes cannot be status changed");
      return;
    }
    if (!grn.status) return;
    setStatusConfirmModal({
      isOpen: true,
      targetStatus: grn.status,
      isFormSubmit: false,
      pendingPayload: null,
    });
  };

  const handleSubmit = () => {
    if (isCompleted) {
      toast.error("Completed Goods Received Notes cannot be edited");
      return;
    }
    if (!validateFields()) return;
    const updatedGrn = { ...grn, store_id: grn.store_id || store_id };

    // If status changed, confirm with user before proceeding
    const initialStatus = grnData?.status?.toLowerCase();
    const nextStatus = updatedGrn.status?.toLowerCase();
    if (
      hasPermission("goods received note update_status") &&
      nextStatus &&
      initialStatus &&
      nextStatus !== initialStatus
    ) {
      setStatusConfirmModal({
        isOpen: true,
        targetStatus: updatedGrn.status,
        isFormSubmit: true,
        pendingPayload: updatedGrn,
      });
      return;
    }

    updateGrnMutation.mutate(updatedGrn);
  };

  const handleGrnChange = (key, value) => {
    setGrn({ ...grn, [key]: value });
  };

  const calculateTotalAmount = (items = grnItems) => {
    let subtotal = 0;
    let totalTax = 0;
    let grandTotal = 0;

    items.forEach((item) => {
      const qty = parseFloat(item.received_quantity) || 0;
      const cost = parseFloat(item.unit_cost) || 0;
      const taxAmount = parseFloat(item.tax_amount) || 0;
      const discountAmount = parseFloat(item.discount_amount) || 0;
      const isFoc = item.is_foc || false;

      // FOC items are free - exclude their cost from subtotal (not a discount)
      if (!isFoc) {
        subtotal += qty * cost - discountAmount;
        totalTax += taxAmount;
      }
      grandTotal += parseFloat(item.total_cost) || 0;
    });

    setGrn((prev) => ({
      ...prev,
      sub_total_amount: subtotal,
      total_tax: totalTax,
      total_amount: grandTotal,
    }));
  };

  useEffect(() => {
    if (grnItems.length) calculateTotalAmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnItems]);

  const grnItemChange = (index, key, value, extraProduct) => {
    const newItems = [...grnItems];
    newItems[index][key] = value;

    // Auto-update quantity for serial products
    if (key === "serial_numbers") {
      newItems[index].received_quantity = (value || []).length;
    }

    // Map PO field data or general product field data when product is selected
    if (key === "product_id" && value) {
      const selectedPOItem = orderProducts.find(
        (p) => String(p.product_id) === String(value),
      );
      if (selectedPOItem) {
        const qtyToReceive = Math.max(
          0,
          (selectedPOItem.ordered_qty || 0) -
          (selectedPOItem.received_qty || 0),
        );
        newItems[index].product = selectedPOItem.product;
        newItems[index].unit_cost = parseFloat(selectedPOItem.unit_cost) || 0;
        newItems[index].unit_sell_price =
          selectedPOItem.unit_sell_price || newItems[index].unit_cost;
        newItems[index].unit_sell_price_min =
          selectedPOItem.unit_sell_price_min || newItems[index].unit_cost;
        newItems[index].unit_sell_price_max =
          selectedPOItem.unit_sell_price_max || newItems[index].unit_cost;
        newItems[index].tax_rate = parseFloat(
          selectedPOItem.tax_percentage || 0,
        );
        newItems[index].tax_percentage = newItems[index].tax_rate;
        newItems[index].tax_method = (
          selectedPOItem.tax_method || "exclusive"
        ).toLowerCase();
        newItems[index].tax_id = selectedPOItem.tax_id || null;
        newItems[index].tax_class = selectedPOItem.tax_class || "";
        newItems[index].order_quantity = selectedPOItem.ordered_qty || 0;
        newItems[index].already_received_quantity =
          selectedPOItem.received_qty || 0;
        newItems[index].received_quantity = qtyToReceive;
        newItems[index].is_foc = selectedPOItem.is_foc || false;
        newItems[index].discount_type = (
          selectedPOItem.discount_type || "fixed"
        ).toLowerCase();
        newItems[index].discount_value =
          parseFloat(
            selectedPOItem.discount_value || selectedPOItem.discount,
          ) || 0;
        newItems[index].notes =
          selectedPOItem.notes ||
          selectedPOItem.public_notes ||
          selectedPOItem.internal_notes ||
          "";
        newItems[index].meta = selectedPOItem.meta || [];
      } else {
        // Non-PO product: fetch info from product object or searched data
        const prod =
          extraProduct ||
          newItems[index].product ||
          searchedProductsData.find((p) => String(p.id) === String(value));

        const cost = parseFloat(
          prod?.cost_price || prod?.cost || prod?.unit_cost || 0,
        );
        const sellPrice = parseFloat(
          prod?.selling_price || prod?.price || prod?.unit_sell_price || cost,
        );

        newItems[index].product = prod;
        newItems[index].order_quantity = 0;
        newItems[index].already_received_quantity = 0;
        const isSerial =
          prod?.tracking_type === "serial" || prod?.is_serialized;
        if (
          !isSerial &&
          (!newItems[index].received_quantity ||
            parseFloat(newItems[index].received_quantity) <= 0)
        ) {
          newItems[index].received_quantity = 1;
        }
        newItems[index].unit_cost = cost;
        newItems[index].unit_sell_price = sellPrice;
        newItems[index].unit_sell_price_min = parseFloat(
          prod?.selling_price_min || prod?.min_price || cost,
        );
        newItems[index].unit_sell_price_max = parseFloat(
          prod?.selling_price_max || prod?.max_price || sellPrice,
        );
        const taxPct = parseFloat(
          prod?.tax?.rate || prod?.tax_percentage || prod?.tax_rate || 0,
        );
        newItems[index].tax_rate = taxPct;
        newItems[index].tax_percentage = taxPct;
        newItems[index].tax_method = (
          prod?.tax_method || "exclusive"
        ).toLowerCase();
        newItems[index].tax_id = prod?.tax_id || null;
        newItems[index].tax_class = prod?.tax_class || "";
        newItems[index].is_foc = false;
        newItems[index].discount_type = "fixed";
        newItems[index].discount_value = 0;
        newItems[index].notes = "";
        newItems[index].meta = [];
      }
    } else if (key === "product_id" && !value) {
      newItems[index].product = null;
      newItems[index].order_quantity = 0;
      newItems[index].already_received_quantity = 0;
      newItems[index].received_quantity = 0;
      newItems[index].unit_cost = 0;
      newItems[index].unit_sell_price = 0;
      newItems[index].tax_percentage = 0;
      newItems[index].discount_value = 0;
      newItems[index].serial_numbers = [];
    }

    // Recalculate line total if any relevant field changes
    if (
      [
        "received_quantity",
        "unit_cost",
        "tax_percentage",
        "tax_method",
        "discount_type",
        "discount_value",
        "is_foc",
        "product_id",
        "serial_numbers",
      ].includes(key)
    ) {
      const qty = parseFloat(newItems[index].received_quantity) || 0;
      const cost = parseFloat(newItems[index].unit_cost) || 0;
      const isFoc = newItems[index].is_foc || false;

      let discountAmount = 0;
      let taxAmount = 0;
      let lineTotal = 0;

      if (isFoc) {
        discountAmount = qty * cost;
        taxAmount = 0;
        lineTotal = 0;
      } else {
        const discType = (
          newItems[index].discount_type || "fixed"
        ).toLowerCase();
        const discValue = parseFloat(newItems[index].discount_value) || 0;
        const taxRate = parseFloat(newItems[index].tax_percentage || 0);
        const taxMethod = (
          newItems[index].tax_method || "exclusive"
        ).toLowerCase();

        if (discType === "percentage") {
          discountAmount = (qty * cost * discValue) / 100;
        } else {
          discountAmount = discValue;
        }

        const subtotalAfterDiscount = qty * cost - discountAmount;
        if (taxMethod === "exclusive") {
          taxAmount = subtotalAfterDiscount * (taxRate / 100);
          lineTotal = subtotalAfterDiscount + taxAmount;
        } else {
          // Price already includes tax - do not extract; align with sales tax behaviour
          taxAmount = 0;
          lineTotal = subtotalAfterDiscount;
        }
      }

      newItems[index].discount_amount = discountAmount;
      newItems[index].tax_amount = taxAmount;
      newItems[index].total_cost = lineTotal;
    }

    // Sync unit sell prices if cost changes and they are unset
    if (key === "unit_cost" && !newItems[index].unit_sell_price) {
      newItems[index].unit_sell_price = newItems[index].unit_cost;
      newItems[index].unit_sell_price_min = newItems[index].unit_cost;
      newItems[index].unit_sell_price_max = newItems[index].unit_cost;
    }

    setGrnItems(newItems);
  };

  const addNewItem = () => {
    if (grnItems.length >= 25) {
      toast.error("You cannot add more than 25 items.");
      return;
    }
    setGrnItems([
      ...grnItems,
      {
        product_id: "",
        order_quantity: 0,
        already_received_quantity: 0,
        received_quantity: 0,
        received_quantity_type: "good",
        unit_cost: 0,
        unit_sell_price: 0,
        unit_sell_price_min: 0,
        unit_sell_price_max: 0,
        tax_id: null,
        tax_class: "",
        tax_percentage: 0,
        tax_amount: 0,
        tax_method: "Exclusive",
        discount_type: "Fixed",
        discount_value: 0,
        discount_amount: 0,
        is_foc: false,
        total_cost: 0,
        notes: "",
        serial_numbers: [],
        meta: [],
      },
    ]);
  };

  const removeItem = (index) => {
    const item = grnItems[index];
    if (item.id) {
      deleteItemMutation.mutate(item.id);
    } else {
      const newItems = [...grnItems];
      newItems.splice(index, 1);
      setGrnItems(newItems);
    }
  };

  if (grnLoading || itemsLoading)
    return (
      <div className="flex h-[400px] w-full items-center justify-center">
        <Spinner size={48} />
      </div>
    );

  return (
    <div className="page-wrapper">
      <div className="content p-6 max-w-[100vw] overflow-x-hidden">
        <PageHeader>
          <PageHeaderHeading
            title="Edit GRN"
            subtitle="Edit Goods Received Note"
          />
          <PageHeaderActions>
            <Button variant="outline" onClick={() => navigate(all_routes.grn)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            {hasPermission("report goods received note view") && (
              <Button
                variant="outline"
                onClick={handleGeneratePDF}
                disabled={pdfLoading}
              >
                <FileText className="mr-2 h-4 w-4" />
                {pdfLoading ? "Generating..." : "Generate PDF"}
              </Button>
            )}
            {isCompleted ? (
              <Badge
                variant="outline"
                className="border-emerald-500 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1.5 text-xs font-semibold gap-1.5"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Completed (Read-Only)
              </Badge>
            ) : (
              hasPermission("goods received note update") && (
                <Button
                  onClick={handleSubmit}
                  disabled={updateGrnMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {updateGrnMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              )
            )}
          </PageHeaderActions>
        </PageHeader>

        <div className="space-y-6">
          {isCompleted && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50/90 dark:bg-amber-950/40 dark:border-amber-900/60 text-amber-900 dark:text-amber-200">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-sm">Completed Goods Received Note</h4>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  This Goods Received Note has been marked as <strong>Completed</strong> and is locked. Completed GRNs cannot be edited, nor can their status be reverted.
                </p>
              </div>
            </div>
          )}
          {/* Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>GRN Information</CardTitle>
              <CardDescription>
                Update details for the Goods Received Note.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Purchase Order */}
                <div className="space-y-2">
                  <Label>
                    Purchase Order <span className="text-red-500">*</span>
                  </Label>
                  <ReactSelect
                    options={orders.map((order) => ({
                      value: String(order.id),
                      label: order.tracking_number,
                    }))}
                    value={
                      grn.purchase_order_id
                        ? orders
                          .map((o) => ({
                            value: String(o.id),
                            label: o.tracking_number,
                          }))
                          .find(
                            (opt) =>
                              opt.value === String(grn.purchase_order_id),
                          ) ||
                        {
                          value: String(grn.purchase_order_id),
                          label:
                            grnData?.purchaseOrder?.tracking_number ||
                            grnData?.purchase_order?.tracking_number ||
                            String(grn.purchase_order_id),
                        }
                        : null
                    }
                    onInputChange={(value) => setPoSearchInput(value)}
                    filterOption={() => true}
                    isLoading={isOrdersFetching}
                    onChange={(opt) =>
                      handleGrnChange("purchase_order_id", opt ? opt.value : "")
                    }
                    placeholder="Search Purchase Order..."
                  />
                </div>

                {/* ReceivedDate */}
                <div className="space-y-2">
                  <Label>Received Date</Label>
                  <Input
                    type="date"
                    value={grn.received_date ? grn.received_date.split("T")[0] : ""}
                    onChange={(e) =>
                      handleGrnChange("received_date", e.target.value)
                    }
                  />
                </div>

                {/* Received By */}
                <div className="space-y-2">
                  <Label>Received By</Label>
                  <ReactSelect
                    onInputChange={(value) => setEmployeeSearchInput(value)}
                    isLoading={isEmployeesFetching}
                    filterOption={() => true}
                    options={employees.map((emp) => ({
                      value: String(emp.id),
                      label: emp.name || emp.full_name,
                    }))}
                    value={
                      grn.received_by
                        ? {
                          value: String(grn.received_by),
                          label:
                            employeeLabels[String(grn.received_by)] ||
                            employees
                              .map((e) => ({
                                value: String(e.id),
                                label: e.name || e.full_name,
                              }))
                              .find(
                                (opt) =>
                                  opt.value === String(grn.received_by),
                              )?.label ||
                            "Select Employee",
                        }
                        : null
                    }
                    onChange={(opt) =>
                      handleGrnChange("received_by", opt ? opt.value : "")
                    }
                    placeholder="Select Employee"
                  />
                </div>

                {/* GRN Status */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>
                      GRN Status <span className="text-red-500">*</span>
                    </Label>
                    {!isCompleted &&
                      hasPermission("goods received note update_status") &&
                      grn.status &&
                      grnData?.status &&
                      grn.status.toLowerCase() !==
                      grnData.status.toLowerCase() && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="h-6 text-xs px-2"
                          onClick={handleUpdateStatusOnlyClick}
                          disabled={updateStatusMutation.isPending}
                        >
                          {updateStatusMutation.isPending ? (
                            <>
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              Updating Status...
                            </>
                          ) : (
                            "Update Status Only"
                          )}
                        </Button>
                      )}
                  </div>
                  <ReactSelect
                    isDisabled={isCompleted || !hasPermission("goods received note update_status")}
                    options={
                      isCompleted
                        ? [{ value: "completed", label: "Completed" }]
                        : [
                          { value: "pending", label: "Pending" },
                          { value: "completed", label: "Completed" },
                          { value: "cancelled", label: "Cancelled" },
                        ]
                    }
                    value={
                      grn.status
                        ? [
                          { value: "pending", label: "Pending" },
                          { value: "completed", label: "Completed" },
                          { value: "cancelled", label: "Cancelled" },
                        ].find(
                          (opt) => opt.value === grn.status.toLowerCase(),
                        )
                        : null
                    }
                    onChange={(opt) =>
                      !isCompleted &&
                      handleGrnChange("status", opt ? opt.value : "pending")
                    }
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {isCompleted
                      ? "Completed GRNs are finalized and cannot be modified or status changed."
                      : "Status can be updated independently without updating the rest of the GRN."}
                  </p>
                </div>

                {/* Document */}
                <div className="space-y-2">
                  <Label>Document</Label>
                  <Input
                    value={grn.document || ""}
                    onChange={(e) =>
                      handleGrnChange("document", e.target.value)
                    }
                  />
                </div>

                {/* Payment Method */}
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <ReactSelect
                    options={availablePaymentMethods.map((pm) => ({
                      value: pm.slug || pm.id || pm.name,
                      label: pm.name,
                    }))}
                    value={
                      grn.payment_method
                        ? {
                          value: grn.payment_method,
                          label:
                            availablePaymentMethods.find(
                              (pm) => pm.id === grn.payment_method || pm.slug === grn.payment_method || pm.name === grn.payment_method
                            )?.name ||
                            grnData?.paymentMethod?.name ||
                            grnData?.payment_method?.name ||
                            (typeof grn.payment_method === "string" ? grn.payment_method : "Select Payment Method"),
                        }
                        : null
                    }
                    onChange={(opt) =>
                      handleGrnChange("payment_method", opt ? opt.value : "")
                    }
                    isLoading={paymentMethodsLoading}
                    placeholder="Select Payment Method"
                    isClearable
                  />
                </div>

                {/* Document Type */}
                <div className="space-y-2">
                  <Label>Document Type</Label>
                  <ReactSelect
                    options={[
                      { value: "Invoice", label: "Invoice" },
                      { value: "Delivery Note", label: "Delivery Note" },
                      { value: "Packing List", label: "Packing List" },
                      { value: "Other", label: "Other" },
                    ]}
                    value={
                      grn.document_type
                        ? {
                          value: grn.document_type,
                          label: grn.document_type,
                        }
                        : null
                    }
                    onChange={(opt) =>
                      handleGrnChange("document_type", opt ? opt.value : "")
                    }
                  />
                </div>

                {/* Document Status */}
                <div className="space-y-2">
                  <Label>Document Status</Label>
                  <ReactSelect
                    options={[
                      { value: "Original", label: "Original" },
                      { value: "Copy", label: "Copy" },
                      { value: "Electronic", label: "Electronic" },
                    ]}
                    value={
                      grn.document_status
                        ? {
                          value: grn.document_status,
                          label: grn.document_status,
                        }
                        : null
                    }
                    onChange={(opt) =>
                      handleGrnChange("document_status", opt ? opt.value : "")
                    }
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Reference / Internal Tracking</Label>
                  <Input
                    value={grn.reference || ""}
                    onChange={(e) =>
                      handleGrnChange("reference", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="mt-6">
                <Label>Notes</Label>
                <Textarea
                  value={grn.notes || ""}
                  onChange={(e) => handleGrnChange("notes", e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* GRN Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-muted-foreground" />
                <CardTitle>GRN Items</CardTitle>
              </div>
              {!isCompleted && hasPermission("goods received note item create") && (
                <Button onClick={addNewItem} size="sm" type="button" disabled={isCompleted || grnItems.length >= 25}>
                  <Plus className="mr-2 h-4 w-4" /> Add Item
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead className="min-w-[200px]">Product</TableHead>
                      <TableHead className="w-[80px]">Order</TableHead>
                      <TableHead className="w-[80px]">Recv</TableHead>
                      <TableHead className="w-[100px]">Condition</TableHead>
                      <TableHead className="w-[150px]">Tracking Info</TableHead>
                      <TableHead className="w-[110px]">
                        Cost ({currencySymbol})
                      </TableHead>
                      <TableHead className="w-[130px]">Adjustment</TableHead>
                      <TableHead className="w-[120px]">
                        Line Total ({currencySymbol})
                      </TableHead>
                      <TableHead className="w-[80px] text-center">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grnItems.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="text-center py-6 text-muted-foreground"
                        >
                          No items added.
                        </TableCell>
                      </TableRow>
                    ) : (
                      grnItems.map((item, index) => {
                        const isPoItem = orderProducts.some(
                          (p) =>
                            String(p.product_id) === String(item.product_id),
                        );
                        const trackingType =
                          item.product?.tracking_type ||
                          (item.product?.is_serialized ? "serial" : "none");

                        return (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <ReactSelect
                                  options={productOptions}
                                  isLoading={isProductsFetching}
                                  onInputChange={(val, { action }) => {
                                    if (action === "input-change") {
                                      setProductSearchInput(val);
                                    } else if (
                                      action === "menu-close" ||
                                      action === "set-value"
                                    ) {
                                      setProductSearchInput("");
                                    }
                                  }}
                                  filterOption={() => true}
                                  value={(() => {
                                    if (!item.product_id) return null;
                                    const p =
                                      item.product ||
                                      orderProducts.find(
                                        (op) =>
                                          String(op.product_id) ===
                                          String(item.product_id),
                                      )?.product ||
                                      searchedProductsData.find(
                                        (sp) =>
                                          String(sp.id) ===
                                          String(item.product_id),
                                      );
                                    const label = p
                                      ? `${p.name || p.product_name || "Product"}${p.sku ? ` (${p.sku})` : ""}`
                                      : productLabels[String(item.product_id)] ||
                                      "Select Product";
                                    return {
                                      value: String(item.product_id),
                                      label,
                                    };
                                  })()}
                                  onChange={(opt) => {
                                    grnItemChange(
                                      index,
                                      "product_id",
                                      opt ? opt.value : "",
                                      opt?.product,
                                    );
                                    if (opt) {
                                      setProductLabels((prev) => ({
                                        ...prev,
                                        [opt.value]: opt.label,
                                      }));
                                    }
                                  }}
                                  formatOptionLabel={(opt, { context }) => {
                                    if (context === "value") {
                                      return opt.label;
                                    }
                                    return (
                                      <div className="flex items-center justify-between w-full py-0.5 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <span className="font-medium text-sm truncate">
                                            {opt.product?.name ||
                                              opt.product?.product_name ||
                                              opt.label}
                                          </span>
                                          {opt.product?.sku && (
                                            <span className="text-[11px] text-muted-foreground font-mono bg-muted/80 px-1.5 py-0.5 rounded shrink-0">
                                              {opt.product.sku}
                                            </span>
                                          )}
                                        </div>
                                        {opt.isPoItem ? (
                                          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0">
                                            PO Qty: {opt.poItem?.ordered_qty ?? 0}
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                                            Inventory
                                          </span>
                                        )}
                                      </div>
                                    );
                                  }}
                                  noOptionsMessage={() =>
                                    productSearchInput?.trim()
                                      ? "No matching products found"
                                      : "Type to search all inventory products..."
                                  }
                                  placeholder="Search product name or SKU..."
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                  isClearable
                                />
                                <div className="flex flex-wrap items-center gap-1">
                                  {item.product_id && !isPoItem && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-purple-50 text-purple-700 border-purple-200"
                                    >
                                      Non-PO Item
                                    </Badge>
                                  )}
                                  {item.product?.brand?.name && (
                                    <Badge
                                      variant="secondary"
                                      className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                    >
                                      {item.product.brand.name}
                                    </Badge>
                                  )}
                                  {(item.product?.category?.name || item.product?.categories?.[0]?.name) && (
                                    <Badge
                                      variant="secondary"
                                      className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                    >
                                      {item.product.category?.name || item.product.categories[0].name}
                                    </Badge>
                                  )}
                                  {item.product?.unit?.short_name && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                    >
                                      {item.product.unit.short_name}
                                    </Badge>
                                  )}
                                  {item.product?.warehouse?.name && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                    >
                                      {item.product.warehouse.name}
                                      {item.product.warehouse?.location?.name ? ` (${item.product.warehouse.location.name})` : ""}
                                    </Badge>
                                  )}
                                  {trackingType === "serial" && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-blue-50/50 text-blue-600 border-blue-200"
                                    >
                                      <ScanBarcode className="h-3 w-3 mr-1" />
                                      Serial Tracking
                                    </Badge>
                                  )}
                                  {trackingType === "standard" && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"
                                    >
                                      <Package className="h-3 w-3 mr-1" />
                                      Batch Tracking
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Input
                                readOnly
                                value={item.order_quantity ?? 0}
                                className="h-9 bg-muted"
                                title={
                                  isPoItem
                                    ? "Ordered quantity in PO"
                                    : "Not ordered in PO (0)"
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.received_quantity}
                                onChange={(e) =>
                                  grnItemChange(
                                    index,
                                    "received_quantity",
                                    e.target.value,
                                  )
                                }
                                readOnly={trackingType === "serial"}
                                className={`h-9 ${trackingType === "serial"
                                  ? "bg-muted cursor-not-allowed"
                                  : ""
                                  }`}
                              />
                            </TableCell>
                            <TableCell>
                              <ReactSelect
                                options={[
                                  { value: "good", label: "Good" },
                                  { value: "damaged", label: "Damaged" },
                                  { value: "short", label: "Short" },
                                  { value: "expired", label: "Expired" },
                                ]}
                                value={{
                                  value: item.received_quantity_type || "good",
                                  label:
                                    (item.received_quantity_type || "Good")
                                      .charAt(0)
                                      .toUpperCase() +
                                    (
                                      item.received_quantity_type || "good"
                                    ).slice(1),
                                }}
                                onChange={(opt) =>
                                  grnItemChange(
                                    index,
                                    "received_quantity_type",
                                    opt.value,
                                  )
                                }
                                menuPortalTarget={document.body}
                              />
                            </TableCell>

                            <TableCell>
                              {trackingType === "standard" && (
                                <div className="space-y-1">
                                  <Input
                                    placeholder="Batch #"
                                    value={item.batch_number || ""}
                                    onChange={(e) =>
                                      grnItemChange(
                                        index,
                                        "batch_number",
                                        e.target.value,
                                      )
                                    }
                                    className="h-8 text-xs"
                                  />
                                  <Input
                                    type="date"
                                    value={
                                      item.expiry_date
                                        ? item.expiry_date.split("T")[0]
                                        : ""
                                    }
                                    onChange={(e) =>
                                      grnItemChange(
                                        index,
                                        "expiry_date",
                                        e.target.value,
                                      )
                                    }
                                    className="h-8 text-xs"
                                  />
                                </div>
                              )}
                              {trackingType === "serial" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openSerialModal(index)}
                                  className={`w-full h-8 text-[10px] gap-1 ${(item.serial_numbers?.length || 0) ===
                                    parseInt(item.received_quantity || 0)
                                    ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                                    : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                    }`}
                                >
                                  <ScanBarcode className="h-3 w-3" />
                                  {item.serial_numbers?.length || 0} /{" "}
                                  {item.received_quantity || 0} Serials
                                </Button>
                              )}
                              {trackingType === "none" && (
                                <span className="text-xs text-muted-foreground italic">
                                  Not tracked
                                </span>
                              )}
                            </TableCell>

                            <TableCell>
                              <div className="space-y-1">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.unit_cost}
                                  onChange={(e) =>
                                    grnItemChange(
                                      index,
                                      "unit_cost",
                                      e.target.value,
                                    )
                                  }
                                  className={`h-9 ${item.is_foc ? "bg-muted italic" : ""
                                    }`}
                                  readOnly={item.is_foc}
                                />
                                {item.is_foc && (
                                  <Badge className="w-full justify-center bg-green-50 text-green-700 border-green-200 text-[9px] h-4">
                                    FREE OF CHARGE
                                  </Badge>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex flex-wrap gap-1">
                                  {item.is_foc ? (
                                    <span className="text-[10px] text-green-600 font-medium bg-green-50 px-1 rounded">
                                      -100% (FOC)
                                    </span>
                                  ) : (
                                    <>
                                      {parseFloat(item.discount_amount || 0) >
                                        0 && (
                                          <span className="text-[10px] text-red-600 font-medium bg-red-50 px-1 rounded">
                                            Disc: -
                                            {parseFloat(
                                              item.discount_amount,
                                            ).toFixed(2)}
                                          </span>
                                        )}
                                      <span className="text-[10px] text-blue-600 font-medium bg-blue-50 px-1 rounded">
                                        Tax:{" "}
                                        {parseFloat(
                                          item.tax_percentage || 0,
                                        ).toFixed(1)}
                                        %
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div className="text-[9px] text-muted-foreground italic truncate">
                                  {item.tax_method} |{" "}
                                  {item.tax_class || "No Class"}
                                </div>
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="space-y-1 text-right">
                                <Input
                                  readOnly
                                  value={parseFloat(
                                    item.total_cost || 0,
                                  ).toFixed(2)}
                                  className="h-9 bg-muted text-right font-medium"
                                />
                                {parseFloat(item.tax_amount || 0) > 0 && (
                                  <div className="text-[10px] text-muted-foreground px-1">
                                    Incl. Tax: +
                                    {parseFloat(item.tax_amount).toFixed(2)}
                                  </div>
                                )}
                                {item.is_foc && (
                                  <div className="text-[10px] text-muted-foreground px-1">
                                    Free of Charge
                                  </div>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                {!isCompleted &&
                                  hasPermission(
                                    "goods received note item update",
                                  ) && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                                      onClick={() =>
                                        setConfigModal({
                                          isOpen: true,
                                          itemIndex: index,
                                        })
                                      }
                                    >
                                      <Settings className="h-4 w-4" />
                                    </Button>
                                  )}
                                {!isCompleted &&
                                  hasPermission(
                                    "goods received note item delete",
                                  ) && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => removeItem(index)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
            {grnItems.length > 0 && (
              <CardFooter className="flex flex-col items-end py-4 border-t gap-2">
                <div className="w-full max-w-xs flex justify-between items-center text-muted-foreground">
                  <span className="text-sm">Subtotal:</span>
                  <span className="text-sm">
                    {currencySymbol}
                    {parseFloat(grn.sub_total_amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="w-full max-w-xs flex justify-between items-center text-muted-foreground">
                  <span className="text-sm">Total Tax:</span>
                  <span className="text-sm">
                    {currencySymbol}
                    {parseFloat(grn.total_tax || 0).toFixed(2)}
                  </span>
                </div>
                <div className="w-full max-w-xs flex justify-between items-center font-bold text-lg pt-2 border-t">
                  <span>Grand Total:</span>
                  <span>
                    {currencySymbol}
                    {parseFloat(grn.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              </CardFooter>
            )}
          </Card>
        </div>

        {/* PDF Ready Dialog */}
        <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
          <DialogContent onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>PDF Generated Successfully</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center p-4 space-y-4">
              <div className="text-green-600">
                <FileText size={48} />
              </div>
              <p className="text-center text-muted-foreground">
                Your GRN PDF is ready to download or print.
              </p>
              <div className="flex gap-4">
                <Button onClick={handleDownloadPDF}>
                  <Download className="mr-2 h-4 w-4" /> Download
                </Button>
                <Button variant="secondary" onClick={handlePrintPDF}>
                  <Printer className="mr-2 h-4 w-4" /> Print
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Status Change Confirmation Alert */}
        <AlertDialog
          open={statusConfirmModal.isOpen}
          onOpenChange={(open) =>
            !open &&
            setStatusConfirmModal({
              isOpen: false,
              targetStatus: null,
              isFormSubmit: false,
              pendingPayload: null,
            })
          }
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Confirm Status Change
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-foreground/80 mt-2">
                  <p>
                    Are you sure you want to change the status of this Goods Received Note to{" "}
                    <span className="font-semibold capitalize text-foreground">
                      {statusConfirmModal.targetStatus}
                    </span>
                    ?
                  </p>
                  <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900/50 p-3 text-xs text-amber-800 dark:text-amber-200 space-y-1">
                    <p className="font-semibold flex items-center gap-1.5">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                      Action is Non-Reversible
                    </p>
                    <p>
                      {statusConfirmModal.targetStatus === "cancelled" ? (
                        <>
                          Once marked as <strong>Cancelled</strong>, the GRN cannot be edited, nor can its status be changed or reverted back to <strong>Pending or Completed</strong>.
                        </>
                      ) : statusConfirmModal.targetStatus === "completed" ? (
                        <>
                          Once marked as <strong>Completed</strong>, the GRN cannot be edited, nor can its status be changed or reverted back to <strong>Pending or Cancelled</strong>.
                        </>
                      ) : (
                        <>
                          Are you sure you want to change the status to <strong>Pending</strong>?
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={
                  updateStatusMutation.isPending || updateGrnMutation.isPending
                }
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={
                  updateStatusMutation.isPending || updateGrnMutation.isPending
                }
                onClick={(e) => {
                  e.preventDefault();
                  const { isFormSubmit, pendingPayload, targetStatus } =
                    statusConfirmModal;
                  setStatusConfirmModal({
                    isOpen: false,
                    targetStatus: null,
                    isFormSubmit: false,
                    pendingPayload: null,
                  });
                  if (isFormSubmit && pendingPayload) {
                    updateGrnMutation.mutate(pendingPayload);
                  } else if (targetStatus) {
                    updateStatusMutation.mutate(targetStatus);
                  }
                }}
                className={
                  statusConfirmModal.targetStatus === "completed"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : statusConfirmModal.targetStatus === "cancelled"
                      ? "bg-rose-600 hover:bg-rose-700 text-white"
                      : ""
                }
              >
                {updateStatusMutation.isPending || updateGrnMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  `Confirm & Set as ${statusConfirmModal.targetStatus ? statusConfirmModal.targetStatus.charAt(0).toUpperCase() + statusConfirmModal.targetStatus.slice(1) : "Status"}`
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add Serials Modal */}
        <AddSerialsModal
          isOpen={serialModal.isOpen}
          onClose={() => setSerialModal((prev) => ({ ...prev, isOpen: false }))}
          onSave={handleSaveSerials}
          initialSerials={serialModal.initialSerials}
          quantity={serialModal.quantity}
          productName={serialModal.productName}
          excludeSerials={excludeSerials}
        />

        {/* Item Configuration Modal */}
        <GRNItemConfigModal
          isOpen={configModal.isOpen}
          onClose={() => setConfigModal({ isOpen: false, itemIndex: null })}
          item={
            configModal.itemIndex !== null
              ? grnItems[configModal.itemIndex]
              : null
          }
          onUpdate={(updatedData) => {
            const index = configModal.itemIndex;
            Object.entries(updatedData).forEach(([key, value]) => {
              grnItemChange(index, key, value);
            });
          }}
        />
      </div>
    </div>
  );
};

const GRNItemConfigModal = ({ isOpen, onClose, item, onUpdate }) => {
  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Item Configuration</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          {/* FOC Toggle */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border col-span-2">
            <div className="space-y-0.5">
              <Label className="text-base">Free of Charge (FOC)</Label>
              <p className="text-xs text-muted-foreground">
                Mark this item as free. Cost will be set to 0 and tax will be
                removed.
              </p>
            </div>
            <Switch
              checked={item.is_foc || false}
              onCheckedChange={(val) => onUpdate({ is_foc: val })}
            />
          </div>

          {!item.is_foc && (
            <>
              {/* Discount Section */}
              <div className="space-y-2 border p-3 rounded-lg bg-muted/10">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Discount
                </Label>
                <div className="flex gap-2">
                  <div className="w-[120px]">
                    <ReactSelect
                      options={[
                        { value: "Fixed", label: "Fixed" },
                        { value: "Percentage", label: "Percentage" },
                      ]}
                      value={{
                        value: item.discount_type || "Fixed",
                        label: item.discount_type || "Fixed",
                      }}
                      onChange={(opt) => onUpdate({ discount_type: opt.value })}
                      className="text-sm"
                    />
                  </div>
                  <Input
                    type="number"
                    value={item.discount_value || 0}
                    onChange={(e) =>
                      onUpdate({ discount_value: e.target.value })
                    }
                    placeholder="Value"
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Tax Section */}
              <div className="space-y-2 border p-3 rounded-lg bg-muted/10">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Taxation
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    value={item.tax_percentage || 0}
                    onChange={(e) =>
                      onUpdate({ tax_percentage: e.target.value })
                    }
                    placeholder="Percentage"
                  />
                  <ReactSelect
                    options={[
                      { value: "Exclusive", label: "Exclusive" },
                      { value: "Inclusive", label: "Inclusive" },
                    ]}
                    value={{
                      value: item.tax_method || "Exclusive",
                      label: item.tax_method || "Exclusive",
                    }}
                    onChange={(opt) => onUpdate({ tax_method: opt.value })}
                  />
                </div>
              </div>
            </>
          )}

          {/* Selling Prices Section */}
          <div className="col-span-2 space-y-3 py-2 border-t mt-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Selling Prices (Unit)
            </Label>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground">
                  Base Price
                </Label>
                <Input
                  type="number"
                  value={item.unit_sell_price || 0}
                  onChange={(e) =>
                    onUpdate({ unit_sell_price: e.target.value })
                  }
                  placeholder="Base"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground">
                  Min Price
                </Label>
                <Input
                  type="number"
                  value={item.unit_sell_price_min || 0}
                  onChange={(e) =>
                    onUpdate({ unit_sell_price_min: e.target.value })
                  }
                  placeholder="Min"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground">
                  Max Price
                </Label>
                <Input
                  type="number"
                  value={item.unit_sell_price_max || 0}
                  onChange={(e) =>
                    onUpdate({ unit_sell_price_max: e.target.value })
                  }
                  placeholder="Max"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="col-span-2 space-y-2 py-2 border-t mt-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Item Notes
            </Label>
            <Textarea
              value={item.notes || ""}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              placeholder="Internal notes for this item..."
              className="h-20"
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditGRN;
